import { tr } from '@/lib/i18n-core';
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import type { OrderLinePriority } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DateTimePicker, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { DraftOrderList } from './DraftOrderList';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { OrderablePicker } from './OrderablePicker';
import { ADMISSION_NOTES_MAX, addToDraft, inHouseTotalMinor, ionisingInDraft, isAdmissionScope, linesNotDoneHere, mayBePregnant, placementBody, placementPath } from './draft';
import type { DraftLine, OrderKind, OrderPatient, OrderScope } from './types';

type PregnancyAnswer = '' | 'not_pregnant' | 'pregnant';

export function OrderComposer({
  kind,
  scope,
  patient,
  onPlaced,
  initialSearch,
}: {
  kind: OrderKind;
  scope: OrderScope;
  patient: OrderPatient;
  onPlaced: () => void;
  initialSearch?: string;
}) {
  const toast = useToast();
  const admission = isAdmissionScope(scope);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [priority, setPriority] = useState<OrderLinePriority>('routine');
  const [indication, setIndication] = useState('');
  const [collectAt, setCollectAt] = useState('');
  const [pregnancy, setPregnancy] = useState<PregnancyAnswer>('');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const quantities = useMemo(() => new Map(lines.map((line) => [line.chargeItemId, line.quantity])), [lines]);
  const askPregnancy = kind === 'radiology' && ionisingInDraft(lines) && mayBePregnant(patient);
  const pregnancyReady = !askPregnancy || pregnancy === 'not_pregnant' || (pregnancy === 'pregnant' && justification.trim().length >= 5);
  const anyInHouse = lines.some((line) => line.fulfilment === 'in_house');
  const notDoneHere = admission ? linesNotDoneHere(lines) : [];

  const reset = () => {
    setLines([]);
    setIndication('');
    setCollectAt('');
    setPregnancy('');
    setJustification('');
    setPriority('routine');
  };

  const place = useMutation({
    mutationFn: () =>
      post(
        placementPath(kind, scope),
        placementBody(kind, scope, patient.id, {
          lines,
          priority,
          indication,
          collectAt,
          pregnancy: askPregnancy ? pregnancy : '',
          pregnancyJustification: justification,
        }),
      ),
    onSuccess: () => {
      toast.success(tr('opd', `order_placed_${kind}`), admission ? tr('opd', 'io_ord_placed_stay') : anyInHouse ? tr('opd', 'order_placed_in_house') : tr('opd', 'order_placed_outside'));
      reset();
      onPlaced();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', `order_new_${kind}`)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="grid gap-4 lg:grid-cols-2">
          <OrderablePicker kind={kind} quantities={quantities} initialSearch={initialSearch} onPick={(row) => setLines((current) => addToDraft(current, row))} />
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold">{tr('opd', 'order_draft_title')}</h3>
              {anyInHouse && <span className="text-xs text-muted-foreground">{tr('opd', 'order_billed_here_x', { amount: money(inHouseTotalMinor(lines)) })}</span>}
            </div>
            <DraftOrderList kind={kind} lines={lines} admission={admission} onChange={setLines} />
          </div>
        </div>

        {notDoneHere.length > 0 && <Alert variant="warning">{tr('opd', 'io_ord_blocked_outside')}</Alert>}

        <div className="grid gap-4 md:grid-cols-2">
          <Field label={tr('opd', 'priority')}>
            <ChoiceButtons<OrderLinePriority>
              value={priority}
              onChange={setPriority}
              options={[
                { value: 'routine', label: tr('opd', 'routine') },
                { value: 'urgent', label: tr('opd', 'urgent'), tone: 'warning' },
                { value: 'stat', label: tr('opd', 'stat'), tone: 'destructive' },
              ]}
            />
          </Field>
          {kind === 'lab' && anyInHouse && (
            <Field label={tr('opd', 'collect_at')} hint={tr('opd', 'leave_empty_to_collect_now_e_g_fasting_sugar')}>
              <DateTimePicker value={collectAt} onChange={(value) => setCollectAt(value)} />
            </Field>
          )}
        </div>

        {askPregnancy && (
          <div className="space-y-2 rounded-xl border border-warning/40 bg-warning/5 p-3 text-sm">
            <p className="font-medium">{tr('opd', 'could_the_patient_be_pregnant_x_ray_ct_and')}</p>
            <div className="sm:w-80">
              <ChoiceButtons<PregnancyAnswer>
                value={pregnancy}
                onChange={setPregnancy}
                options={[
                  { value: 'not_pregnant', label: tr('opd', 'not_pregnant') },
                  { value: 'pregnant', label: tr('opd', 'pregnant_proceed'), tone: 'destructive' },
                ]}
              />
            </div>
            {pregnancy === 'pregnant' && (
              <Field label={tr('opd', 'why_the_study_goes_ahead')} required>
                <Input value={justification} maxLength={600} onChange={(event) => setJustification(event.target.value)} />
              </Field>
            )}
          </div>
        )}

        <Field label={tr('opd', 'order_indication')} hint={tr('opd', 'order_indication_hint')}>
          <Textarea rows={2} maxLength={admission ? ADMISSION_NOTES_MAX : 2000} value={indication} onChange={(event) => setIndication(event.target.value)} />
        </Field>

        <div className="flex justify-end">
          <Button
            onClick={() => {
              setError(null);
              place.mutate();
            }}
            loading={place.isPending}
            disabled={lines.length === 0 || !pregnancyReady || notDoneHere.length > 0}
          >
            <Send className="h-4 w-4" aria-hidden /> {tr('opd', 'order_place_x', { count: lines.length })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
